import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Search, RefreshCw, Loader2, Filter, X, ArrowUp, ArrowDown, ArrowUpDown, ExternalLink, ChevronDown, Check } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import OppStageTimeline from "@/components/OppStageTimeline";
import CsvExportPopover from "@/components/CsvExportPopover";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "@/components/ui/sonner";
import { exportCSV } from "@/lib/csv";
import { useAuth } from "../contexts/AuthContext";

const API = `${window.location.origin}/api`;

// Stage candidati per il filtro, suddivisi per pipeline (Reparto Clinico
// vs Ortodonzia). La pagina mostra solo gli stage della pipeline selezionata
// nel dropdown precedente.
const STAGE_OPTIONS_CLINICO = [
  { value: "", label: "Tutti gli stage" },
  { value: "Da autorizzare", label: "Da autorizzare" },
  { value: "Caricati", label: "Caricati" },
  { value: "Caricati da paziente", label: "Caricati da paziente" },
  { value: "Autorizzati", label: "Autorizzati" },
  { value: "In esecuzione", label: "In esecuzione" },
  { value: "Completati", label: "Completati" },
  { value: "Controllo pagamento", label: "Controllo pagamento" },
  { value: "A rimborso", label: "A rimborso" },
  // Categorie virtuali per le opp delle pipeline "Indiretti" (vengono
  // filtrate via flag `is_indiretto` invece che via stage name).
  { value: "__indiretto__", label: "Indiretto" },
  { value: "__indiretto_completato__", label: "Indiretto completato" },
];

const STAGE_OPTIONS_ORTO = [
  { value: "", label: "Tutti gli stage" },
  { value: "Preventivi da caricare", label: "Preventivi da caricare" },
  { value: "Preventivi caricati", label: "Preventivi caricati" },
  { value: "Preventivi da eseguire", label: "Preventivi da eseguire" },
  { value: "Preventivi in corso", label: "Preventivi in corso" },
  { value: "Preventivi eseguiti", label: "Preventivi eseguiti" },
  { value: "__indiretto__", label: "Indiretto" },
  { value: "__indiretto_completato__", label: "Indiretto completato" },
];

// Set di valori virtuali (non corrispondono a stage GHL reali, sono
// scorciatoie per filtrare le opp delle pipeline "Indiretti").
const VIRTUAL_STAGE_VALUES = new Set(["__indiretto__", "__indiretto_completato__"]);

const PIPELINE_OPTIONS = [
  { value: "clinico", label: "Reparto Clinico" },
  { value: "orto", label: "Ortodonzia" },
];

function fmtDateShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
}

const fmtEuro = (n) => {
  if (n == null || isNaN(n)) return "—";
  return Number(n).toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const fmtDiffEuro = (n) => {
  if (n == null || isNaN(n)) return "—";
  // Forza il segno per i valori negativi (nativo it-IT) e mostra "0" senza segno per 0.
  const v = Number(n);
  if (v === 0) return "0 €";
  return v.toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0, signDisplay: "exceptZero" });
};

const fmtDiffPct = (n) => {
  if (n == null || isNaN(n)) return "—";
  const v = Number(n);
  if (v === 0) return "0%";
  return v.toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 1, signDisplay: "exceptZero" }) + "%";
};

// "2026-05" -> "Maggio 2026"
const fmtMonthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("it-IT", { month: "long", year: "numeric" })
    .replace(/^\w/, (c) => c.toUpperCase());
};

// Mappa status GHL → etichetta italiana per la colonna STATO
const STATUS_LABEL = {
  open: "Aperta",
  won: "Vinta",
  lost: "Persa",
  abandoned: "Abbandonata",
};
const STATUS_CLASS = {
  open: "bg-amber-100 text-amber-700",
  won: "bg-emerald-100 text-emerald-700",
  lost: "bg-rose-100 text-rose-700",
  abandoned: "bg-slate-100 text-slate-600",
};

// Dropdown multi-select per gli stage. Popover con checkbox.
// `value` è array di string (stage names). Vuoto = "tutti".
function MultiStageFilter({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) => {
    if (value.includes(v)) onChange(value.filter((x) => x !== v));
    else onChange([...value, v]);
  };
  const allSelected = value.length === 0;
  const label = allSelected
    ? "Tutti gli stage"
    : value.length === 1
    ? value[0]
    : `${value.length} stage selezionati`;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="storico-stage-filter"
          className="h-10 text-sm border border-slate-200 rounded-md px-3 bg-white flex items-center justify-between gap-2 hover:border-slate-300 focus:outline-none focus:border-blue-400 transition-colors"
        >
          <span className={`truncate ${allSelected ? "text-slate-500" : "text-slate-800 font-medium"}`}>
            {label}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-1" align="start">
        <button
          type="button"
          onClick={() => onChange([])}
          className="w-full text-left px-2 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-50 rounded flex items-center gap-2"
          data-testid="storico-stage-clear"
        >
          <X className="w-3.5 h-3.5" /> Mostra tutti
        </button>
        <div className="h-px bg-slate-100 my-1" />
        <div className="max-h-72 overflow-y-auto">
          {options.map((opt) => {
            const checked = value.includes(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggle(opt.value)}
                data-testid={`storico-stage-opt-${opt.value}`}
                className={`w-full text-left px-2 py-1.5 text-sm rounded flex items-center gap-2 hover:bg-blue-50 ${
                  checked ? "text-slate-900" : "text-slate-700"
                }`}
              >
                <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                  checked ? "bg-blue-600 border-blue-600" : "border-slate-300"
                }`}>
                  {checked && <Check className="w-3 h-3 text-white" />}
                </span>
                <span className="truncate">{opt.label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}


// TH cliccabile con indicatore freccia. Tre stati:
//   neutro (icona ↑↓ grigia) → click → asc (freccia ↑) → click → desc (↓)
//   → click → torna a neutro.
function SortableTh({ sortKey, type, onSort, sort, w, align = "left", title, testId, className = "", children }) {
  const active = sort.key === sortKey;
  const dir = active ? sort.dir : null;
  const Icon = dir === "asc" ? ArrowUp : dir === "desc" ? ArrowDown : ArrowUpDown;
  return (
    <th
      style={{ width: w }}
      className={`px-1.5 py-1.5 whitespace-nowrap ${align === "center" ? "text-center" : "text-left"} select-none cursor-pointer hover:text-slate-600 transition-colors ${className}`}
      onClick={() => onSort(sortKey, type)}
      title={title}
      data-testid={testId}
    >
      <span className={`inline-flex items-center gap-0.5 ${align === "center" ? "justify-center w-full" : ""}`}>
        <span>{children}</span>
        <Icon className={`w-2.5 h-2.5 flex-shrink-0 ${active ? "text-blue-600" : "text-slate-300"}`} />
      </span>
    </th>
  );
}

export default function StoricoStagePage({ mode = "panel" }) {
  const isArchivio = mode === "archivio";
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // URL params iniziali (es. deep-link da Chiama Paziente):
  //   ?q=<opp_id|patient_name>  → pre-compila la ricerca
  //   ?pipeline=clinico|orto    → pre-seleziona la pipeline
  const _urlParams = typeof window !== "undefined"
    ? new URLSearchParams(window.location.search)
    : null;
  const _initialQ = _urlParams?.get("q") || "";
  const _initialPipelineParam = (_urlParams?.get("pipeline") || "").toLowerCase();
  const _initialPipeline = _initialPipelineParam === "orto" ? "orto" : "clinico";
  const [q, setQ] = useState(_initialQ);
  const [debouncedQ, setDebouncedQ] = useState(_initialQ);
  const [pipeline, setPipeline] = useState(_initialPipeline);
  // Stage filter: array di stage names. Vuoto = nessun filtro (tutti).
  // Selezione multipla via popover con checkbox.
  const [stages, setStages] = useState([]);
  // Lo "status" non è più scelto dall'utente: il Pannello mostra SOLO
  // "open", l'Archivio mostra SOLO "won". Manteniamo lo state per
  // riusare la stessa logica di filtro `displayedItems`.
  const statusFilter = isArchivio ? "won" : "open";
  const [monthFilter, setMonthFilter] = useState("");     // "" | "YYYY-MM" su data_inserimento
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [restoring, setRestoring] = useState(false);
  // Ordinamento per colonna: { key, dir, type }
  // dir: "asc" | "desc"  ·  type: "string" | "date" | "number"
  // Click header → asc → desc → null (off)
  const [sort, setSort] = useState({ key: null, dir: null, type: null });

  const toggleSort = (key, type) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc", type };
      if (prev.dir === "asc") return { key, dir: "desc", type };
      return { key: null, dir: null, type: null };
    });
  };

  // Debounce sulla ricerca testuale per ridurre il numero di chiamate
  // mentre l'utente digita.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedQ) params.q = debouncedQ;
      if (pipeline) params.pipeline = pipeline;
      // Gli stage virtuali "Indiretto"/"Indiretto completato" non sono
      // stage GHL reali — non li passiamo al backend, filtreremo
      // client-side via `is_indiretto` + status.
      const realStages = stages.filter((s) => !VIRTUAL_STAGE_VALUES.has(s));
      if (realStages.length === 1) params.stage = realStages[0];
      const res = await axios.get(`${API}/dashboard/opp-history-search`, {
        params,
        withCredentials: true,
      });
      setItems(res.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento storico");
    } finally {
      setLoading(false);
    }
  }, [debouncedQ, pipeline, stages]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Ordinamento client-side stabile, con i null/undefined sempre in coda
  // (sia in asc sia in desc) — comportamento intuitivo per le date vuote
  // e i KPI non ancora calcolati. Arricchisce ogni item con `diff_eur` e
  // `diff_pct` (negativi quando l'assicurazione paga meno del listino BDD).
  const enrichedItems = useMemo(() => {
    return items.map((it) => {
      const ins = Number(it.total_cost);
      const bdd = Number(it.total_cost_base);
      let diffEur = null;
      let diffPct = null;
      if (Number.isFinite(ins) && Number.isFinite(bdd) && bdd > 0) {
        diffEur = ins - bdd;       // negativo se l'assicurazione paga meno
        diffPct = (diffEur / bdd) * 100;
      }
      return { ...it, diff_eur: diffEur, diff_pct: diffPct };
    });
  }, [items]);

  const sortedItems = useMemo(() => {
    if (!sort.key) return enrichedItems;
    const dir = sort.dir === "desc" ? -1 : 1;
    const type = sort.type;
    const key = sort.key;
    const isEmpty = (v) => v === null || v === undefined || v === "";
    return [...enrichedItems].sort((a, b) => {
      const va = a?.[key];
      const vb = b?.[key];
      const ea = isEmpty(va);
      const eb = isEmpty(vb);
      if (ea && eb) return 0;
      if (ea) return 1;   // vuoti sempre in coda
      if (eb) return -1;
      if (type === "number") return (Number(va) - Number(vb)) * dir;
      if (type === "boolean") return ((va ? 1 : 0) - (vb ? 1 : 0)) * dir;
      if (type === "date") {
        const da = new Date(va).getTime();
        const db = new Date(vb).getTime();
        if (isNaN(da) || isNaN(db)) return String(va).localeCompare(String(vb), "it") * dir;
        return (da - db) * dir;
      }
      return String(va).localeCompare(String(vb), "it", { sensitivity: "base" }) * dir;
    });
  }, [enrichedItems, sort]);

  // Filtri secondari client-side (stato + mese inserimento).
  // L'API ritorna già pipeline/stage/q, ma stato e mese li applichiamo
  // qui per evitare un round-trip e per riusare la stessa data list
  // anche quando l'utente sperimenta con i filtri.
  const displayedItems = useMemo(() => {
    return sortedItems.filter((it) => {
      // Filtro stages: gli stage "veri" (es. "Da autorizzare") matchano
      // su `current_stage`. I valori virtuali "__indiretto__" /
      // "__indiretto_completato__" sono OR logico sui flag.
      if (stages.length >= 1) {
        const realStages = stages.filter((s) => !VIRTUAL_STAGE_VALUES.has(s));
        const wantIndiretto = stages.includes("__indiretto__");
        const wantIndirettoCompletato = stages.includes("__indiretto_completato__");
        const matchReal = realStages.length === 0 ? false : realStages.includes(it.current_stage);
        const matchIndiretto = wantIndiretto && it.is_indiretto && it.current_status !== "won";
        const matchIndirettoCompl = wantIndirettoCompletato && it.is_indiretto && it.current_status === "won";
        if (!matchReal && !matchIndiretto && !matchIndirettoCompl) return false;
      }
      if (statusFilter) {
        const s = it.current_status || "";
        if (statusFilter === "open" && s !== "open") return false;
        if (statusFilter === "won" && s !== "won") return false;
      }
      if (monthFilter) {
        const di = (it.data_inserimento || "").slice(0, 7); // "YYYY-MM"
        if (di !== monthFilter) return false;
      }
      return true;
    });
  }, [sortedItems, stages, statusFilter, monthFilter]);

  // Lista dei mesi YYYY-MM presenti nei dati (per popolare il dropdown
  // mese). Ordinati dal più recente al più vecchio.
  const monthOptions = useMemo(() => {
    const set = new Set();
    items.forEach((it) => {
      const di = (it.data_inserimento || "").slice(0, 7);
      if (di) set.add(di);
    });
    return Array.from(set).sort().reverse();
  }, [items]);

  // Totali aggregati delle 4 colonne economiche per il footer.
  // Mostrato solo quando almeno uno tra stage / stato / mese è attivo
  // (richiesta esplicita dell'utente).
  const showTotalsRow = !!(stages.length || statusFilter || monthFilter);
  const totals = useMemo(() => {
    if (!showTotalsRow) return null;
    let importo = 0, bdd = 0, count = 0, countWithBoth = 0, excludedCount = 0;
    displayedItems.forEach((it) => {
      // Le opp marcate `archive_excluded` (= falsi positivi) NON entrano
      // nei totali Importo/BDD. Le contiamo a parte per mostrare il flag
      // in UI accanto al label "Totali".
      if (it.archive_excluded) {
        excludedCount++;
        return;
      }
      const ins = Number(it.total_cost);
      const b = Number(it.total_cost_base);
      if (Number.isFinite(ins)) { importo += ins; count++; }
      if (Number.isFinite(ins) && Number.isFinite(b) && b > 0) {
        bdd += b;
        countWithBoth++;
      }
    });
    const diff = countWithBoth > 0 ? (importo - bdd) : null;
    // Per la % usiamo il rapporto sui soli items con BDD disponibile,
    // per evitare di falsare la media con opp che non hanno BDD calcolato.
    const pct = (countWithBoth > 0 && bdd > 0) ? ((importo - bdd) / bdd) * 100 : null;
    return { importo, bdd: countWithBoth > 0 ? bdd : null, diff, pct, count, countWithBoth, excludedCount };
  }, [displayedItems, showTotalsRow]);

  const filtersActive = !!(debouncedQ || pipeline !== "clinico" || stages.length || monthFilter);
  const resetFilters = () => {
    setQ(""); setPipeline("clinico"); setStages([]);
    setMonthFilter("");
  };

  const handleExportCSV = (period, offset, label) => {
    if (!items.length) {
      toast.error("Nessun dato da esportare");
      return;
    }
    // Calcola il range [rangeStart, rangeEnd] in base a period/offset.
    // Filtriamo le opp per `data_inserimento` (campo GHL "Data di
    // inserimento opportunità"). period="all" → no filter.
    const now = new Date();
    let rangeStart = null, rangeEnd = null;
    if (period === "week") {
      const monday = new Date(now);
      const day = monday.getDay() || 7;
      monday.setDate(monday.getDate() - day + 1 + offset * 7);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      rangeStart = monday.toISOString().slice(0, 10);
      rangeEnd = sunday.toISOString().slice(0, 10);
    } else if (period === "month") {
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      const targetMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      rangeStart = `${targetMonth}-01`;
      const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      rangeEnd = `${targetMonth}-${String(last).padStart(2, "0")}`;
    }
    const filtered = displayedItems.filter((it) => {
      if (!rangeStart) return true;
      const di = (it.data_inserimento || "").slice(0, 10);
      if (!di) return false;
      return di >= rangeStart && di <= rangeEnd;
    });
    if (!filtered.length) {
      toast.error("Nessun dato per il periodo selezionato");
      return;
    }
    const isOrto = pipeline === "orto";
    const baseCols = [
      { key: "patient_name", label: "Paziente" },
      { key: "opp_id", label: "Opp ID" },
      { key: "assicurazione_name", label: "Assicurazione" },
      { key: "total_cost", label: "Importo" },
      { key: "total_cost_base", label: "BDD" },
      { key: "diff_eur", label: "Differenza" },
      { key: "diff_pct", label: "% Diff." },
      { key: "pipeline_tag", label: "Pipeline",
        format: (r) => (r.pipeline_tag === "clinico" ? "Reparto Clinico" : "Ortodonzia") },
      { key: "is_indiretto", label: "Indiretto",
        format: (r) => (r.is_indiretto ? "Sì" : "No") },
      { key: "current_stage", label: "Stage attuale" },
      { key: "current_status", label: "Stato",
        format: (r) => (STATUS_LABEL[r.current_status] || r.current_status || "") },
      { key: "data_inserimento", label: "Inseriti" },
    ];
    const dateCols = isOrto
      ? [
          { key: "caricato_at", label: "Preventivi caricati",
            format: (r) => (r.caricato_at ? r.caricato_at.slice(0, 10) : "") },
          { key: "autorizzato_at", label: "Preventivi in corso",
            format: (r) => (r.autorizzato_at ? r.autorizzato_at.slice(0, 10) : "") },
          { key: "completato_at", label: "Preventivi eseguiti",
            format: (r) => (r.completato_at ? r.completato_at.slice(0, 10) : "") },
        ]
      : [
          { key: "autorizzato_at", label: "Autorizzati",
            format: (r) => (r.autorizzato_at ? r.autorizzato_at.slice(0, 10) : "") },
          { key: "in_esecuzione_at", label: "In esecuzione",
            format: (r) => (r.in_esecuzione_at ? r.in_esecuzione_at.slice(0, 10) : "") },
          { key: "completato_at", label: "Completato",
            format: (r) => (r.completato_at ? r.completato_at.slice(0, 10) : "") },
        ];
    const kpiCols = isOrto
      ? [
          { key: "days_inseriti_to_caricato", label: "Giorni Ins. → Caricato" },
          { key: "days_inseriti_to_autorizzato", label: "Giorni Ins. → In corso" },
          { key: "days_inseriti_to_completato", label: "Giorni Ins. → Eseguito" },
        ]
      : [
          { key: "days_inseriti_to_autorizzato", label: "Giorni Ins. → Aut." },
          { key: "days_inseriti_to_completato", label: "Giorni Ins. → Compl." },
          { key: "days_inseriti_to_chiusura", label: "Giorni Ins. → Chius." },
        ];
    const columns = [
      ...baseCols,
      ...dateCols,
      { key: "data_chiusura_prevista", label: "Chiusura prevista" },
      ...kpiCols,
    ];
    const periodSuffix = period === "all" ? "tutti" : (rangeStart || "").replace(/-/g, "");
    exportCSV(
      `storico-stage-${isOrto ? "ortodonzia" : "clinico"}-${periodSuffix}`,
      columns,
      filtered,
    );
    toast.success(`Esportate ${filtered.length} opp — ${label}`);
  };

  // Ripristino opportunità (admin-only): ripara i record "correnti" persi
  // dal mass-wipe (fetch GHL fallito) e rilancia la sincronizzazione da GHL
  // in background per recuperare le opp aperte realmente mancanti.
  const handleRestoreOpportunities = async () => {
    if (!window.confirm(
      "Ripristinare le opportunità del Pannello?\n\n" +
      "• Ripara le opportunità scomparse (rimette lo stato 'corrente' sull'ultima versione), preservando le date\n" +
      "• Riavvia la sincronizzazione da GoHighLevel in background per recuperare le opportunità aperte mancanti\n\n" +
      "Operazione sicura e ripetibile."
    )) return;
    setRestoring(true);
    try {
      const { data } = await axios.post(
        `${API}/dashboard/opp-history/restore-and-resync`,
        {},
        { withCredentials: true }
      );
      const r = data.restore || {};
      toast.success(
        `Ripristino completato — opportunità attive: ${r.current_after ?? "?"} ` +
        `(di cui aperte: ${r.current_after_open ?? "?"}). ` +
        `Sincronizzazione da GHL avviata in background: tra ~1 minuto premi "Aggiorna".`
      );
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante il ripristino");
    } finally {
      setRestoring(false);
    }
  };

  // Re-sync admin-only: forza l'aggiornamento del campo `data_inserimento`
  // E dei nomi/cognomi dei pazienti collegati da GHL, per ogni opp
  // tracciata. Sovrascrive il valore locale anche se già popolato.
  const handleResyncDataInserimento = async () => {
    if (!window.confirm(
      "Forzare l'aggiornamento da GHL?\n\n" +
      "L'operazione sincronizza:\n" +
      "• Il campo 'Data di inserimento' di ogni opportunità\n" +
      "• Nome e cognome dei pazienti collegati (utile per i contatti creati oggi)\n\n" +
      "Sovrascrive i valori locali con quelli di GHL."
    )) return;
    setBackfilling(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/resync-data-inserimento`,
        {},
        { withCredentials: true }
      );
      toast.success(
        `Re-sync completato — opp aggiornate ${data.updated} su ${data.processed}` +
        (data.unchanged ? ` (invariate: ${data.unchanged})` : "") +
        (data.missing_in_ghl ? ` (non trovate su GHL: ${data.missing_in_ghl})` : "") +
        ` · Pazienti sincronizzati: ${data.contacts_synced || 0}` +
        (data.contacts_failed ? ` (falliti: ${data.contacts_failed})` : "")
      );
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante il re-sync");
    } finally {
      setBackfilling(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 bg-grid-light pt-[calc(7px+3.5rem+13px)] pb-10 px-4 lg:px-[100px]">
      <Toaster />
      <div className="w-full">
        <PageHeader
          title={isArchivio ? "Archivio Pratiche" : "Pannello Assicurazioni"}
          subtitle={
            isArchivio
              ? "Opportunità chiuse come VINTE — pratiche concluse e archiviate."
              : "Opportunità ancora APERTE — cronologia dei passaggi di stage per Reparto Clinico + Ortodonzia."
          }
          actions={
            <div className="flex items-center gap-2">
              {!isArchivio && isAdmin && (
                <button
                  type="button"
                  onClick={handleRestoreOpportunities}
                  disabled={restoring}
                  data-testid="storico-restore-opportunities"
                  title="Ripristina le opportunità scomparse e risincronizza da GoHighLevel (preserva le date)"
                  className="flex items-center gap-2 bg-emerald-500/90 hover:bg-emerald-500 border border-emerald-300/40 text-white backdrop-blur-sm px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {restoring
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <RefreshCw className="w-4 h-4" />}
                  <span className="text-sm font-medium">
                    {restoring ? "Ripristino in corso…" : "Ripristina opportunità"}
                  </span>
                </button>
              )}
              {!isArchivio && (
                <button
                  type="button"
                  onClick={handleResyncDataInserimento}
                  disabled={backfilling}
                  data-testid="storico-resync-data-inserimento"
                  title="Forza l'aggiornamento da GHL: data di inserimento delle opp + nome/cognome dei pazienti"
                  className="flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white backdrop-blur-sm px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  {backfilling
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <RefreshCw className="w-4 h-4" />}
                  <span className="text-sm font-medium">
                    {backfilling ? "Aggiornamento in corso…" : "Forza"}
                  </span>
                </button>
              )}
              <CsvExportPopover
                title="Esporta storico stage in CSV"
                testIdPrefix="storico-export"
                onDownload={handleExportCSV}
              />
              <button
                type="button"
                onClick={fetchData}
                data-testid="storico-refresh"
                disabled={loading}
                className="flex items-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white backdrop-blur-sm px-4 py-2 rounded-lg disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                <span className="text-sm font-medium">Aggiorna</span>
              </button>
            </div>
          }
        />
      </div>

      <div className="w-full -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_180px_160px_auto] gap-3 items-center">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Cerca per nome paziente o opp_id…"
                className="pl-9"
                data-testid="storico-search"
              />
            </div>
            <select
              value={pipeline}
              onChange={(e) => { setPipeline(e.target.value); setStages([]); }}
              data-testid="storico-pipeline"
              className="h-10 text-sm border border-slate-200 rounded-md px-3 bg-white"
            >
              {PIPELINE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <MultiStageFilter
              options={(pipeline === "orto" ? STAGE_OPTIONS_ORTO : STAGE_OPTIONS_CLINICO).filter((o) => o.value)}
              value={stages}
              onChange={setStages}
            />
            <select
              value={monthFilter}
              onChange={(e) => setMonthFilter(e.target.value)}
              data-testid="storico-month-filter"
              className="h-10 text-sm border border-slate-200 rounded-md px-3 bg-white"
              title="Filtra per mese di inserimento"
            >
              <option value="">Tutti i mesi</option>
              {monthOptions.map((ym) => (
                <option key={ym} value={ym}>{fmtMonthLabel(ym)}</option>
              ))}
            </select>
            {filtersActive && (
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs font-semibold text-rose-600 hover:underline whitespace-nowrap flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Reset
              </button>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-2 flex items-center gap-1">
            <Filter className="w-3 h-3" />
            <span>
              <strong>{displayedItems.length}</strong>
              {displayedItems.length !== items.length ? <> di <strong>{items.length}</strong></> : null}
              {" "}opportunità trovate · click su una riga per vedere la timeline completa
            </span>
          </div>
        </div>

        {loading && items.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Caricamento…
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center text-slate-400">
            Nessuna opportunità nello storico con questi filtri.
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-x-auto">
            <table className="min-w-full text-sm [&_tr>*:first-child]:pl-[20px] [&_tr>*:last-child]:pr-[20px]" data-testid="storico-table">
              <thead>
                <tr className="border-b border-slate-100 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  <SortableTh w="12%" sort={sort} sortKey="patient_name" type="string" onSort={toggleSort} testId="sort-patient">Paziente</SortableTh>
                  <SortableTh w="8%" sort={sort} sortKey="casi_particolari" type="string" onSort={toggleSort} testId="sort-casi-particolari" title="Casi particolari (GHL): Stand-by / No Ticket / No Appuntamento">Casi particolari</SortableTh>
                  <SortableTh w="9%" sort={sort} sortKey="assicurazione_name" type="string" onSort={toggleSort} testId="sort-assicurazione">Assicurazione</SortableTh>
                  <SortableTh w="6%" align="center" sort={sort} sortKey="total_cost" type="number" onSort={toggleSort} testId="sort-importo">Importo</SortableTh>
                  <SortableTh w="6%" align="center" sort={sort} sortKey="total_cost_base" type="number" onSort={toggleSort} testId="sort-importo-base" className="bg-slate-100" title="Costo equivalente delle stesse prestazioni se pagate privatamente (foglio Base)">BDD</SortableTh>
                  <SortableTh w="6%" align="center" sort={sort} sortKey="diff_eur" type="number" onSort={toggleSort} testId="sort-diff-eur" className="bg-slate-100" title="Differenza Importo - BDD (sempre ≤ 0 se l'assicurazione paga meno)">Differenza</SortableTh>
                  <SortableTh w="5%" align="center" sort={sort} sortKey="diff_pct" type="number" onSort={toggleSort} testId="sort-diff-pct" className="bg-slate-100" title="Percentuale di sconto vs listino Base">% Diff.</SortableTh>
                  <th className="text-left px-2 py-2 w-[5%] whitespace-nowrap">Pipeline</th>
                  <SortableTh w="4%" align="center" sort={sort} sortKey="is_indiretto" type="boolean" onSort={toggleSort} testId="sort-indiretto" title="Pratica originata o transitata dalla pipeline Indiretti">Indiretto</SortableTh>
                  <SortableTh w="10%" sort={sort} sortKey="current_stage" type="string" onSort={toggleSort} testId="sort-stage">Stage attuale</SortableTh>
                  <SortableTh w="7%" sort={sort} sortKey="data_inserimento" type="date" onSort={toggleSort} testId="sort-inseriti">Inseriti</SortableTh>
                  {pipeline === "orto" ? (
                    <>
                      <SortableTh w="7%" sort={sort} sortKey="caricato_at" type="date" onSort={toggleSort} testId="sort-caricato" title="Data di prima entrata in Preventivi caricati">Prev. caricati</SortableTh>
                      <SortableTh w="7%" sort={sort} sortKey="autorizzato_at" type="date" onSort={toggleSort} testId="sort-autorizzato" title="Data di prima entrata in Preventivi in corso">Prev. in corso</SortableTh>
                      <SortableTh w="7%" sort={sort} sortKey="completato_at" type="date" onSort={toggleSort} testId="sort-completato" title="Data di prima entrata in Preventivi eseguiti">Prev. eseguiti</SortableTh>
                    </>
                  ) : (
                    <>
                      <SortableTh w="7%" sort={sort} sortKey="autorizzato_at" type="date" onSort={toggleSort} testId="sort-autorizzato" title="Data di prima entrata in Autorizzati">Autorizzati</SortableTh>
                      <SortableTh w="7%" sort={sort} sortKey="in_esecuzione_at" type="date" onSort={toggleSort} testId="sort-in-esecuzione" title="Data di prima entrata in In esecuzione">In esecuzione</SortableTh>
                      <SortableTh w="7%" sort={sort} sortKey="completato_at" type="date" onSort={toggleSort} testId="sort-completato">Completato</SortableTh>
                    </>
                  )}
                  <SortableTh w="7%" sort={sort} sortKey="data_chiusura_prevista" type="date" onSort={toggleSort} testId="sort-chiusura" title="Data di chiusura (effettiva se vinta, altrimenti prevista)">Chiusura</SortableTh>
                  <SortableTh w="6%" sort={sort} sortKey="current_status" type="string" onSort={toggleSort} testId="sort-stato">Stato</SortableTh>
                  {pipeline === "orto" ? (
                    <>
                      <SortableTh w="7%" align="center" sort={sort} sortKey="days_inseriti_to_caricato" type="number" onSort={toggleSort} testId="sort-d-caricato" title="Giorni: Inseriti → Preventivi caricati">Ins. → Caric.</SortableTh>
                      <SortableTh w="7%" align="center" sort={sort} sortKey="days_inseriti_to_autorizzato" type="number" onSort={toggleSort} testId="sort-d-autorizzato" title="Giorni: Inseriti → Preventivi in corso">Ins. → Corso</SortableTh>
                      <SortableTh w="7%" align="center" sort={sort} sortKey="days_inseriti_to_completato" type="number" onSort={toggleSort} testId="sort-d-completato" title="Giorni: Inseriti → Preventivi eseguiti">Ins. → Eseg.</SortableTh>
                    </>
                  ) : (
                    <>
                      <SortableTh w="7%" align="center" sort={sort} sortKey="days_inseriti_to_autorizzato" type="number" onSort={toggleSort} testId="sort-d-autorizzato" title="Giorni: Inseriti → Autorizzato">Ins. → Aut.</SortableTh>
                      <SortableTh w="7%" align="center" sort={sort} sortKey="days_inseriti_to_completato" type="number" onSort={toggleSort} testId="sort-d-completato" title="Giorni: Inseriti → Completato">Ins. → Compl.</SortableTh>
                      <SortableTh w="7%" align="center" sort={sort} sortKey="days_inseriti_to_chiusura" type="number" onSort={toggleSort} testId="sort-d-chiusura" title="Giorni: Inseriti → Chiusura">Ins. → Chius.</SortableTh>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {displayedItems.map((it) => {
                  const fmtDays = (n) => (n == null ? "—" : `${n}g`);
                  const status = it.current_status || "";
                  return (
                    <tr
                      key={it.opp_id}
                      onClick={() => setSelectedOpp(it)}
                      data-testid={`storico-row-${it.opp_id}`}
                      className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                    >
                      <td className="px-2 py-2 text-xs">
                        <div className="font-semibold text-slate-800 truncate capitalize" title={it.patient_name}>
                          {it.patient_name || "(senza nome)"}
                        </div>
                        <div className="text-[10px] text-slate-400 truncate" title={it.opp_id}>
                          {it.opp_id}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-xs" data-testid={`row-casi-particolari-${it.opp_id}`}>
                        {it.casi_particolari ? (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide border whitespace-nowrap ${
                              it.casi_particolari === "Stand-by"
                                ? "bg-amber-50 text-amber-700 border-amber-200"
                                : it.casi_particolari === "No Ticket"
                                ? "bg-rose-50 text-rose-700 border-rose-200"
                                : it.casi_particolari === "No Appuntamento"
                                ? "bg-slate-100 text-slate-700 border-slate-200"
                                : "bg-slate-50 text-slate-600 border-slate-200"
                            }`}
                            title={it.casi_particolari}
                          >
                            {it.casi_particolari}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-700">
                        <div className="truncate" title={it.assicurazione_name || ""}>
                          {it.assicurazione_name || <span className="text-slate-300">—</span>}
                        </div>
                      </td>
                      <td className={`px-2 py-2 text-xs text-center font-semibold tabular-nums whitespace-nowrap ${
                        it.archive_excluded ? "text-rose-600 line-through" : "text-emerald-700"
                      }`} title={it.archive_excluded ? "Esclusa dal totale Incassato" : ""}>
                        {fmtEuro(it.total_cost)}
                      </td>
                      <td className={`px-2 py-2 text-xs text-center font-semibold tabular-nums whitespace-nowrap bg-slate-100 ${
                        it.archive_excluded ? "text-rose-600 line-through" : "text-slate-700"
                      }`} title={it.archive_excluded ? "Esclusa dal totale Incassato" : "Listino BDD (foglio Base)"}>
                        {fmtEuro(it.total_cost_base)}
                      </td>
                      <td className={`px-2 py-2 text-xs text-center font-semibold tabular-nums whitespace-nowrap bg-slate-100 ${
                        it.diff_eur != null && it.diff_eur < 0 ? "text-rose-600" : "text-slate-500"
                      }`} title="Differenza Importo - BDD">
                        {fmtDiffEuro(it.diff_eur)}
                      </td>
                      <td className={`px-2 py-2 text-xs text-center font-semibold tabular-nums whitespace-nowrap bg-slate-100 ${
                        it.diff_pct != null && it.diff_pct < 0 ? "text-rose-600" : "text-slate-500"
                      }`} title="Sconto vs listino BDD">
                        {fmtDiffPct(it.diff_pct)}
                      </td>
                      <td className="px-2 py-2">
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                          it.pipeline_tag === "clinico"
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-blue-100 text-blue-700"
                        }`}>
                          {it.pipeline_tag === "clinico" ? "Clinico" : "Orto"}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center" data-testid={`row-indiretto-${it.opp_id}`}>
                        {it.is_indiretto ? (
                          <span
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-500 text-white text-[11px] font-bold"
                            title="Pratica indiretta"
                          >
                            ✓
                          </span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-xs">
                        {status === "won" ? (
                          <span className="text-slate-300">—</span>
                        ) : (
                          <>
                            <div className="font-semibold text-slate-700 truncate">{it.current_stage || "—"}</div>
                            <div className="text-[10px] text-slate-400">
                              {fmtDateShort(it.last_event)}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-700 tabular-nums">
                        {fmtDateShort(it.data_inserimento)}
                      </td>
                      {pipeline === "orto" ? (
                        <>
                          <td className="px-2 py-2 text-xs text-slate-700 tabular-nums">
                            {fmtDateShort(it.caricato_at)}
                          </td>
                          <td className="px-2 py-2 text-xs text-slate-700 tabular-nums">
                            {fmtDateShort(it.autorizzato_at)}
                          </td>
                          <td className="px-2 py-2 text-xs text-slate-700 tabular-nums">
                            {fmtDateShort(it.completato_at)}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-xs text-slate-700 tabular-nums">
                            {fmtDateShort(it.autorizzato_at)}
                          </td>
                          <td className="px-2 py-2 text-xs text-slate-700 tabular-nums">
                            {fmtDateShort(it.in_esecuzione_at)}
                          </td>
                          <td className="px-2 py-2 text-xs text-slate-700 tabular-nums">
                            {fmtDateShort(it.completato_at)}
                          </td>
                        </>
                      )}
                      <td className="px-2 py-2 text-xs text-slate-700 tabular-nums">
                        {fmtDateShort(it.data_chiusura_prevista)}
                      </td>
                      <td className="px-2 py-2">
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${STATUS_CLASS[status] || "bg-slate-100 text-slate-500"}`}>
                          {STATUS_LABEL[status] || (status || "—")}
                        </span>
                      </td>
                      {pipeline === "orto" ? (
                        <>
                          <td className="px-2 py-2 text-xs text-center tabular-nums text-slate-700">
                            {fmtDays(it.days_inseriti_to_caricato)}
                          </td>
                          <td className="px-2 py-2 text-xs text-center tabular-nums text-slate-700">
                            {fmtDays(it.days_inseriti_to_autorizzato)}
                          </td>
                          <td className="px-2 py-2 text-xs text-center tabular-nums font-semibold text-slate-800">
                            {fmtDays(it.days_inseriti_to_completato)}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-2 py-2 text-xs text-center tabular-nums text-slate-700">
                            {fmtDays(it.days_inseriti_to_autorizzato)}
                          </td>
                          <td className="px-2 py-2 text-xs text-center tabular-nums text-slate-700">
                            {fmtDays(it.days_inseriti_to_completato)}
                          </td>
                          <td className="px-2 py-2 text-xs text-center tabular-nums font-semibold text-slate-800">
                            {fmtDays(it.days_inseriti_to_chiusura)}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              {showTotalsRow && totals && displayedItems.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-300 bg-slate-50 font-bold text-xs text-slate-800">
                    {/* col 1 — Paziente: label totali */}
                    <td className="px-2 py-2 uppercase text-[10px] tracking-wide text-slate-500 whitespace-nowrap" data-testid="totals-label">
                      Totali ({displayedItems.length - (totals.excludedCount || 0)}
                      {totals.excludedCount > 0 && (
                        <span className="text-rose-600 font-semibold normal-case ml-1">
                          (−{totals.excludedCount} esclus{totals.excludedCount === 1 ? "a" : "e"})
                        </span>
                      )}
                      )
                    </td>
                    {/* col 2 — Casi particolari (vuoto) */}
                    <td className="px-2 py-2" />
                    {/* col 3 — Assicurazione (vuoto) */}
                    <td className="px-2 py-2" />
                    {/* col 4 — Importo */}
                    <td className="px-2 py-2 text-center text-emerald-700 tabular-nums whitespace-nowrap" data-testid="totals-importo">
                      {fmtEuro(totals.importo)}
                    </td>
                    {/* col 5 — BDD */}
                    <td className="px-2 py-2 text-center text-slate-700 tabular-nums bg-slate-200/60 whitespace-nowrap" data-testid="totals-bdd">
                      {totals.bdd == null ? "—" : fmtEuro(totals.bdd)}
                    </td>
                    {/* col 6 — Differenza */}
                    <td className={`px-2 py-2 text-center tabular-nums bg-slate-200/60 whitespace-nowrap ${
                      totals.diff != null && totals.diff < 0 ? "text-rose-700" : "text-slate-500"
                    }`} data-testid="totals-diff">
                      {totals.diff == null ? "—" : fmtDiffEuro(totals.diff)}
                    </td>
                    {/* col 7 — % Diff */}
                    <td className={`px-2 py-2 text-center tabular-nums bg-slate-200/60 whitespace-nowrap ${
                      totals.pct != null && totals.pct < 0 ? "text-rose-700" : "text-slate-500"
                    }`} data-testid="totals-pct">
                      {totals.pct == null ? "—" : fmtDiffPct(totals.pct)}
                    </td>
                    {/* Resto (Pipeline, Indiretto, Stage attuale, Inseriti,
                        date varie pipeline, Chiusura, Stato, Giorni ×3) */}
                    <td colSpan={20} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!selectedOpp} onOpenChange={(v) => !v && setSelectedOpp(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="capitalize">{selectedOpp?.patient_name || "Timeline opportunità"}</DialogTitle>
            <DialogDescription className="text-xs">
              {selectedOpp?.pipeline_tag === "clinico" ? "Reparto Clinico" : "Ortodonzia"}
              {selectedOpp?.assicurazione_name ? <> · <strong>{selectedOpp.assicurazione_name}</strong></> : null}
              {selectedOpp?.total_cost != null ? <> · <strong>{fmtEuro(selectedOpp.total_cost)}</strong></> : null}
              {" "}· opp: <span className="font-mono">{selectedOpp?.opp_id}</span>
            </DialogDescription>
          </DialogHeader>
          {selectedOpp && (
            <>
              {/* Link alla scheda piano di cura del paziente. Apre in
                  sola lettura: l'utente vede tutte le prestazioni svolte,
                  foto, importi, ecc. Se l'opp non è stata creata dalla
                  nostra app (no record in `created_opportunities`),
                  l'apertura mostrerà "Opportunità non tracciata localmente". */}
              <div className="mb-4 -mt-1">
                <a
                  href={`/${selectedOpp.pipeline_tag === "clinico" ? "piano-cura-clinico" : "piano-cura-ortodonzia"}?opportunityId=${encodeURIComponent(selectedOpp.opp_id)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3B42DD] hover:text-[#2f36b8] hover:underline"
                  data-testid="storico-open-piano-cura"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Apri scheda piano di cura
                </a>
              </div>
              {/* Toggle "Escludi dal totale" per opp won (archivio).
                  Visibile solo in modalità Archivio Pratiche, perché solo
                  qui vediamo le won. Permette di marcare falsi positivi
                  (es. opp passata per errore in stage "won" e poi
                  cancellata da GHL). L'opp resta nel DB per audit, ma
                  non viene conteggiata nel grafico Incassato. */}
              {isArchivio && selectedOpp.current_status === "won" && (
                <div className="mb-4 p-3 rounded-xl border border-amber-200 bg-amber-50 flex items-center justify-between">
                  <div className="text-xs text-amber-900">
                    <strong>Falso positivo?</strong> Se questa opp è stata
                    erroneamente segnata come vinta in GHL puoi escluderla
                    dal totale Incassato per assicurazione.
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      try {
                        const isExcluded = selectedOpp.archive_excluded;
                        const action = isExcluded ? "include" : "exclude";
                        await axios.post(
                          `${API}/dashboard/won-archive/${action}/${encodeURIComponent(selectedOpp.opp_id)}`,
                          {},
                          { withCredentials: true }
                        );
                        toast.success(isExcluded
                          ? "Opp reinclusa nel totale"
                          : "Opp esclusa dal totale Incassato");
                        // Re-fetch list per aggiornare stato
                        await fetchData();
                        setSelectedOpp(null);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Errore");
                      }
                    }}
                    className="h-8 px-3 text-xs font-semibold border-amber-300 text-amber-700 hover:bg-amber-100"
                    data-testid="toggle-archive-exclude"
                  >
                    {selectedOpp.archive_excluded ? "Reincludi" : "Escludi dal totale"}
                  </Button>
                </div>
              )}
              <OppStageTimeline timeline={selectedOpp.timeline} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
